<template>
  <div class="modal_wrapper active">
    <div class="modal_window white_box create_report_modal">
      <button
        class="close_modal close_btn"
        @click="closeModal()"
      >
        <svg
          fill="none"
          height="32"
          viewBox="0 0 32 32"
          width="32"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M25 7L7 25"
            stroke="#040707"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
          />
          <path
            d="M25 25L7 7"
            stroke="#040707"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
          />
        </svg>
      </button>

      <h3 class="heading_h4 font_bold mb_16">
        {{ widgetModal.widget ? 'Edit' : 'Create' }} Table Widget
      </h3>

      <div :class="'section_wrap '+(settings === 'widget' ? 'open' : '')">
        <div
          class="section_head"
          @click="(settings === 'widget' ? settings = '' : settings = 'widget')"
        >
          <h3 class="section_heading">
            Widget Settings
          </h3>
          <i class="icon arrow_down_icon caret" />
        </div>
        <accordion-transition>
          <div
            v-if="settings === 'widget'"
            class="section_body"
          >
            <p class="desc">
              This widget allows you to copy and paste a table from Excel/Google Sheets. Simply copy the
              data and click the button below. A preview will appear showing you how your widget will look.
            </p>

            <custom-input
              v-model="widget.name"
              label="Widget name"
              placeholder="Enter a name for the widget (optional)"
              width="full"
            />

            <button
              class="btn btn_md primary_btn paste_btn"
              @click="grabFromClipboard()"
            >
              <i class="fas fa-paste" />
              Paste from Clipboard
            </button>
            <small
              v-if="false"
              class="validation"
            >Please copy and paste table data to create a widget</small>
          </div>
        </accordion-transition>
      </div>

      <div
        v-if="widget.attributes.type === 'table'"
        :class="'section_wrap '+(settings === 'table' ? 'open' : '')"
      >
        <div
          class="section_head"
          @click="(settings === 'table' ? settings = '' : settings = 'table')"
        >
          <h3 class="section_heading">
            Table Settings <small>(optional)</small>
          </h3>
          <i class="icon arrow_down_icon caret" />
        </div>
        <accordion-transition>
          <div
            v-if="settings === 'table'"
            class="section_body"
          >
            <custom-select
              v-model="widget.attributes.tableOptions.align"
              :options="{
                'center' : 'Center',
                'start' : 'Left',
                'end' : 'Right'
              }"
              label="Text Align"
              width="full"
            />

            <custom-color-select
              v-model="widget.attributes.tableOptions.headerColor"
              label="Header color"
              placeholder="Enter a hex value E.g. #ffffff"
              width="half"
            />

            <custom-color-select
              v-model="widget.attributes.tableOptions.headerTextColor"
              label="Header text color"
              placeholder="Enter a hex value E.g. #ffffff"
              width="half"
            />

            <custom-color-select
              v-model="widget.attributes.tableOptions.primaryRowColor"
              label="Primary row color"
              placeholder="Enter a hex value E.g. #ffffff"
              width="half"
            />

            <custom-color-select
              v-model="widget.attributes.tableOptions.primaryRowTextColor"
              label="Primary row text color"
              placeholder="Enter a hex value E.g. #ffffff"
              width="half"
            />

            <custom-color-select
              v-model="widget.attributes.tableOptions.secondaryRowColor"
              label="Secondary row color"
              placeholder="Enter a hex value E.g. #ffffff"
              width="half"
            />

            <custom-color-select
              v-model="widget.attributes.tableOptions.secondaryRowTextColor"
              label="Secondary row text color"
              placeholder="Enter a hex value E.g. #ffffff"
              width="half"
            />
          </div>
        </accordion-transition>
      </div>

      <span
        v-show="widget.attributes.options.visibleColumns.length"
        class="preview mb-1"
      >Preview: <small>*First 5 data rows</small></span>
      <div
        v-show="widget.attributes.options.visibleColumns.length"
        id="table-preview"
      />

      <div class="flex items-center section_modal_footer">
        <button
          class="btn btn_md secondary_btn close_modal ml_auto mr_8"
          @click="closeModal()"
        >
          <span>Cancel</span>
        </button>
        <button
          class="btn btn_md primary_btn"
          @click="save()"
        >
          <span>{{ widgetModal.widget ? 'Update' : 'Create' }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import {mapActions, mapState} from 'vuex';
import _ from 'lodash';
import CustomInput from '../../global/forms/CustomInput.vue';
import CustomSelect from '../../global/forms/CustomSelect.vue';
import CustomColorSelect from '../../global/forms/CustomColorSelect.vue';
import AccordionTransition from '../../global/transitions/AccordionTransition.vue';

export default {
  components: {
    CustomInput,
    CustomSelect,
    AccordionTransition,
    CustomColorSelect
  },
  props: {
    validationErrors: {
      type: Object,
      required: false,
      default: null,
    }
  },
  emits: ['refresh-widget'],
  data() {
    return {
      isMounted: false,
      settings: 'widget',
      widget: {
        id: `widget-table-${Date.now()}`,
        name: 'Table Widget',
        type: 'table',
        w: 12,
        h: 4,
        attributes: {
          type: 'table',
          options: {
            visibleColumns: []
          },
          tableOptions: {
            align: 'center',
            headerColor: '#F5F5F5',
            headerTextColor: '#4E4E54',
            primaryRowColor: '#FFFFFF',
            primaryRowTextColor: '#4E4E54',
            secondaryRowColor: '#F8F7F5',
            secondaryRowTextColor: '#4E4E54',
          },
          headers: {},
          rows: []
        },
        content: ''
      },
      createFrom: 'paste',
      firstRowHeaders: true,
    }
  },
  computed: {
    ...mapState(['widgetModal']),
  },
  watch: {
    'widget.attributes.options.tableHeading': {
      handler() {
        // Re-render if heading updated.
        if (this.isMounted && this.widget.attributes.options.visibleColumns.length) this.renderTableFromPaste();
      }
    },
    'widget.attributes.tableOptions': {
      deep: true,
      handler() {
        // Re-render if table options updated.
        if (this.isMounted && this.widget.attributes.options.visibleColumns.length) this.renderTableFromPaste();
      }
    }
  },
  beforeMount() {
    if (this.widgetModal.widget) {
      _.merge(this.widget, this.widgetModal.widget);
      this.widget.attributes.options.visibleColumns = this.widgetModal.widget.attributes.options.visibleColumns;
    }
  },
  mounted() {
    this.isMounted = true;
    if (this.widget.attributes.options.visibleColumns.length) this.renderTableFromPaste();
  },
  methods: {
    ...mapActions('pages', ['addWidget', 'updateWidget']),
    ...mapActions('widgetModal', ['closeModal']),
    addRow() {
      let colCount = this.buildTable.rows[0].length;
      let newRow = 'cell '.repeat(colCount)
      this.buildTable.rows.push(newRow.trim().split(' '));
    },
    addColumn() {
      _.each(this.buildTable.rows, row => {
        row.push('');
      });
    },
    async save() {
      // Set widget content to loading spinner
      this.widget.content = `<div id="${this.widget.id}"><div class="widget-loading"><i class="fa fa-btn fa-spinner fa-spin"></i></div></div>`;

      // Prep a payload for the vuex action
      const payload = {
        widget: this.widget,
        pageId: this.widgetModal.pageId,
      }

      // Check if we're creating a new widget or editing one
      if (!this.widgetModal.widget) {
        // Save the new pages layout (refreshes store pages)
        await this.addWidget(payload);
      } else {
        // Update the widget
        await this.updateWidget(payload);
      }

      // Emit to trigger widget refresh, then close the modal
      this.$emit('refresh-widget', {pageId: this.widgetModal.pageId, widget: this.widget});
      this.closeModal();
    },
    async grabFromClipboard() {
      // Get text from clipboard and convert it to arrays
      const text = await navigator.clipboard.readText();
      this.convertStringToTableArray(text);
    },
    convertStringToTableArray(input) {
      // Parse pasted string, spliting into rows based on \n new line
      var text = input.replace(/\r/g, '').trim('\n');
      var rowsOfText = text.split('\n');
      // Declare variables and reset visibleColumns
      var headers = {};
      var rows = [];
      this.widget.attributes.options.visibleColumns = [];

      // Loop through each row, split into cells using \t tabs
      rowsOfText.forEach((rowAsText, rowNo) => {
        // Remove wrapping double quotes
        var rowArray = rowAsText.split('\t').map(function (colAsText) {
          return colAsText.trim().replace(/^"(.*)"$/, '$1');
        });

        if (rowNo === 0) { // Check if it's header row
          // If so, create a headers object and add each header to visibleColumns
          rowArray.forEach(cell => {
            headers[cell.toLowerCase().replace(/ /g, "_")] = {
              text: cell,
              size: '1fr',
            };
            this.widget.attributes.options.visibleColumns.push(cell.toLowerCase().replace(/ /g, "_"));
          });
        } else {
          // Otherwise, create a row object and push it to the rows array
          let row = {};
          let cellNo = 0;
          _.forEach(headers, (header, index) => {
            row[index] = rowArray[cellNo];
            cellNo++
          });
          rows.push(row);
        }
      });

      // Update widget and render table for preview.
      this.widget.attributes.headers = headers;
      this.widget.attributes.rows = rows
      this.renderTableFromPaste();
    },
    renderTableFromPaste() {
      // Get container and set its height to 100%
      const container = document.getElementById('table-preview');
      container.innerHTML = '';

      // Create table element, apply class and height styling
      const table = document.createElement('div');
      table.classList.add('widget-table');
      table.style.setProperty('height', '100%');

      // Declare array to gather column sizes
      let columnSizes = [];
      _.forEach(this.widget.attributes.headers, header => {
        const headerCell = document.createElement('span');
        headerCell.innerHTML = `<strong>${header.text}</strong>`;
        headerCell.style.justifyContent = this.widget.attributes.tableOptions.align;
        headerCell.style.backgroundColor = this.widget.attributes.tableOptions.headerColor;
        headerCell.style.color = this.widget.attributes.tableOptions.headerTextColor;
        columnSizes.push(header.size);
        table.appendChild(headerCell);
      });
      table.style.gridTemplateColumns = columnSizes.join(' ');

      let secondaryRow = true;
      let limit = 5
      let rowNumber = 1;
      _.forEach(this.widget.attributes.rows, cells => {
        if (rowNumber > limit) {
          return false;
        }
        _.forEach(cells, cell => {
          const rowCell = document.createElement('span');
          rowCell.innerHTML = cell;
          rowCell.style.justifyContent = this.widget.attributes.tableOptions.align;
          if (!secondaryRow) {
            rowCell.style.backgroundColor = this.widget.attributes.tableOptions.primaryRowColor;
            rowCell.style.color = this.widget.attributes.tableOptions.primaryRowTextColor;
          } else {
            rowCell.style.backgroundColor = this.widget.attributes.tableOptions.secondaryRowColor;
            rowCell.style.color = this.widget.attributes.tableOptions.secondaryRowTextColor;
          }
          table.appendChild(rowCell);
        });
        secondaryRow = !secondaryRow;
        rowNumber++;
      });

      container.appendChild(table);
    }
  },
}
</script>

<style scoped src="../../../../reports/css/modal.css" />

<style scoped>
#text-area-1 {
  color: #4E4E54;
  font-size: 14px;
  height: 110px;
  line-height: 1.75;
  padding: 1rem 3rem 1rem 1.5rem;
  border-radius: 1rem;
  display: block;
  width: 100%;
  -webkit-transition: .3s;
  -o-transition: .3s;
  transition: .3s;
  border: 1px solid #E8E5E2;
}

.desc {
  text-align: center;
}

.paste_btn {
  width: 100%;
}

.preview {
  margin-bottom: .75rem;
  font-size: 1rem;
  line-height: 1.3;
  letter-spacing: -0.1px;
  color: #909096;
  padding: 7px 10px;
  border-radius: 3px;
  font-weight: 700;
}

#table-preview {
  width: 100%;
  margin: 10px 0px;
}

.widget-table-heading {
  width: 100%;
  font-size: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: black;
}

.widget-table {
  width: 100%;
  display: grid;
  border-top: 1px solid lightgray;
  border-right: 1px solid lightgray;
  color: black;
}

.widget-table > span {
  display: flex;
  text-align: center;
  align-items: center;
  justify-content: center;
  padding: 8px 4px;
  border-left: 1px solid lightgray;
  border-bottom: 1px solid lightgray;
}

.validation {
  text-align: center;
  color: red;
  margin-left: 10px;
}
</style>
